import { useEffect, useRef } from "react";
import { daysUntilBirthday } from "../utils/dateUtils";

/**
 * useBirthdays
 *
 * Revisa una vez al día si algún miembro del grupo tiene cumpleaños hoy
 * o en 7 días, y muestra un toast de aviso si es el caso.
 *
 * No devuelve nada: su único efecto es llamar a showToast cuando toca.
 *
 * Uso en App.js:
 *   useBirthdays({ user, members, userProfile, lang, showToast });
 */
export function useBirthdays({ user, members, userProfile, lang, showToast }) {
  // Ref para tener siempre la versión más reciente de showToast
  // sin añadirla al array de dependencias (evita re-ejecuciones innecesarias)
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => {
    // No hacemos nada si no hay usuario autenticado o no hay miembros cargados
    if (!user || !members?.length) return;

    // El usuario puede desactivar las notificaciones desde su perfil
    if (userProfile?.birthdayNotifications === false) return;

    // Solo revisamos una vez por día: guardamos la fecha del último chequeo
    const hoy = new Date().toDateString();
    let ultimoChequeo;
    try {
      ultimoChequeo = localStorage.getItem("whislu_bday_check");
    } catch (e) {}

    // Si ya revisamos hoy, no hacemos nada
    if (ultimoChequeo === hoy) return;

    // Marcamos que hoy ya lo revisamos
    try {
      localStorage.setItem("whislu_bday_check", hoy);
    } catch (e) {}

    // Recorremos todos los miembros buscando cumpleaños cercanos
    members.forEach((miembro) => {
      // Saltamos al propio usuario y a miembros sin fecha de cumpleaños
      if (!miembro.birthday || miembro.id === user.uid) return;

      const diasRestantes = daysUntilBirthday(miembro.birthday);

      if (diasRestantes === 7) {
        // Cumpleaños en exactamente una semana
        setTimeout(
          () =>
            showToastRef.current(
              "🎂 " +
                miembro.name +
                (lang === "es"
                  ? " cumple en 7 días!"
                  : "'s birthday is in 7 days!")
            ),
          2000
        );
      } else if (diasRestantes === 0) {
        // ¡Hoy es su cumpleaños!
        setTimeout(
          () =>
            showToastRef.current(
              "🎉 " +
                (lang === "es"
                  ? "¡Hoy es el cumpleaños de " + miembro.name + "!"
                  : "Today is " + miembro.name + "'s birthday!")
            ),
          2000
        );
      }
    });
  }, [user, members, userProfile?.birthdayNotifications, lang]);
}
